import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { Icon } from '../icon';
import { SelectionBox } from './components/bounding-box-selector';
import { CloseIcon } from './components/close-icon';
import { DocViewerLoader } from './components/doc-viewer-loader';
import { DownloadIcon } from './components/download-icon';
import { ExtractFactFromImagePopup } from './components/extract-fact-from-image-popup';
import { ExtractFactsFromSelectionButton } from './components/extract-facts-from-selection-button';
import { FullDocLink } from './components/full-doc-link';
import { ImageRenderer } from './components/image-renderer';
import { PDFDocumentRenderer } from './components/pdf-renderer';
import { SearchBar } from './components/search-bar';
import { SearchButton } from './components/search-button';
import { ShowFactsIcon } from './components/show-facts';
import { Zoom } from './components/zoom';
import { DEFAULT_PAGE_HEIGHT, DEFAULT_PAGE_WIDTH } from './constants';
import { useVisiblePages } from './use-visible-pages';
import { Button } from '../shadcn/button';
import { ButtonGroup } from '../shadcn/button-group';
import { useGetDocChunkMatchesV2 } from '@/api/queries/useGetDocChunkMatchesV2';
import { useGetDocV2 } from '@/api/queries/useGetDocV2';
import { BoundingBox, BoundingBoxWithId } from '@/types';

export interface RendererBaseProps {
  docId: string;

  getPageDimensions: (pageNumber: number) => { height: number; width: number };
  visiblePages: number[];
  getPagePosition: (pageNumber: number) => number;
  totalHeight: number;
  maxPageWidth?: number;

  highlights: BoundingBoxWithId[];
  highlightedFactId?: string | null;
  onHighlightClick?: (id: string) => void;
  highlightedChunkId?: string | null;

  isSelectionMode?: boolean;
  onSelectionComplete?: (
    boundingBox: BoundingBox,
    imageData: string,
    selectionBox: SelectionBox,
    pageNumber: number,
  ) => void;
  maintainSelection?: boolean;
  activeSelection?: { box: SelectionBox; pageNumber: number } | null;
}

export interface PDFViewerRef {
  clearSearch: () => void;
}

interface PDFViewerProps {
  docId: string;
  highlights: BoundingBoxWithId[];
  setHighlightedFactId?: (id: string) => void;
  highlightedFactId?: string | null;
  onHighlightClick?: (id: string) => void;
  onViewerClick?: () => void;
  closeViewer?: () => void;
  hideFeatureButtons?: boolean;
  hideSnippingTool?: boolean;
  showFacts?: boolean;
  setShowFacts?: (show: boolean) => void;
  fileName?: string;
  initialSearchQuery?: string;
}

export const PDFViewer = forwardRef<PDFViewerRef, PDFViewerProps>(
  (
    {
      docId,
      highlights,
      closeViewer,
      highlightedFactId,
      setHighlightedFactId,
      onHighlightClick,
      onViewerClick,
      hideFeatureButtons,
      hideSnippingTool,
      showFacts,
      setShowFacts,
      fileName,
      initialSearchQuery,
    },
    ref,
  ) => {
    const { matterId } = useParams();

    // Selection mode state
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [popupData, setPopupData] = useState<{
      isOpen: boolean;
      imageBase64: string;
      boundingBox: BoundingBox | null;
      anchorPosition: { x: number; y: number; pageNumber: number } | null;
      side: 'left' | 'right';
    }>({
      isOpen: false,
      imageBase64: '',
      boundingBox: null,
      anchorPosition: null,
      side: 'left',
    });
    const [activeSelection, setActiveSelection] = useState<{ box: SelectionBox; pageNumber: number } | null>(null);

    // Search state
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
    const [showSearchBar, setShowSearchBar] = useState(!!initialSearchQuery);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [highlightedChunkId, setHighlightedChunkId] = useState<string | null>(null);

    // Zoom state - start with 1, will be updated when doc loads
    const [zoom, setZoom] = useState(1);
    const [userHasChangedZoom, setUserHasChangedZoom] = useState(false);
    const pendingScrollRef = useRef<{ top: number; left: number } | null>(null);

    const handleZoom = useCallback(
      (delta: number) => {
        if (!containerRef.current) return;

        // Mark that user has manually changed zoom
        setUserHasChangedZoom(true);

        const container = containerRef.current;
        const prevZoom = zoom;
        const prevScrollTop = container.scrollTop;
        const prevScrollLeft = container.scrollLeft;

        // Calculate the center point of the viewport
        const viewportCenterY = prevScrollTop + container.clientHeight / 2;
        const viewportCenterX = prevScrollLeft + container.clientWidth / 2;

        // For Y: we need to account for the fact that page gaps (10px) don't scale with zoom
        // Page content height at zoom=1 is DEFAULT_PAGE_HEIGHT, so at prevZoom it's DEFAULT_PAGE_HEIGHT * prevZoom
        const pageHeightWithGap = DEFAULT_PAGE_HEIGHT * prevZoom + 10;

        // Estimate which "page position" we're at (as a continuous value)
        const approxPagePosition = viewportCenterY / pageHeightWithGap;

        // Separate the scalable content position from the fixed gap position
        // Number of gaps passed = floor of page position (gaps come after each page)
        const gapsAbove = Math.floor(approxPagePosition);
        const totalGapHeight = gapsAbove * 10;

        // The content portion (everything except gaps) should scale
        const contentY = viewportCenterY - totalGapHeight;
        const normalizedContentY = contentY / prevZoom;

        // Calculate new zoom using multiplicative scaling for consistent feel at all zoom levels
        // delta is treated as a percentage: positive = zoom in, negative = zoom out
        const zoomFactor = 1 + delta;
        const newZoom = Math.max(0.2, Math.min(zoom * zoomFactor, 3));

        // Calculate new scroll position: scale the content, keep gaps fixed
        const newContentY = normalizedContentY * newZoom;
        const newScrollTop = newContentY + totalGapHeight - container.clientHeight / 2;

        // For X: simpler since there are no gaps horizontally
        const docPositionX = viewportCenterX / prevZoom;
        const newScrollLeft = docPositionX * newZoom - container.clientWidth / 2;

        // Store pending scroll and update zoom - scroll will be applied in useLayoutEffect
        pendingScrollRef.current = { top: Math.max(0, newScrollTop), left: Math.max(0, newScrollLeft) };
        setZoom(newZoom);
      },
      [zoom, setZoom],
    );

    // Apply pending scroll after zoom state change causes DOM to update
    useLayoutEffect(() => {
      if (pendingScrollRef.current && containerRef.current) {
        containerRef.current.scrollTop = pendingScrollRef.current.top;
        containerRef.current.scrollLeft = pendingScrollRef.current.left;
        pendingScrollRef.current = null;
      }
    }, [zoom]);

    const handleZoomIn = () => handleZoom(0.1);
    const handleZoomOut = () => handleZoom(-0.1);

    // Outer container
    const containerRef = useRef<HTMLDivElement>(null);

    // Get info about the doc
    const { data: doc, isLoading: isDocLoading } = useGetDocV2(matterId, docId);
    const hasBeenConvertedToImages = doc?.file?.hasBeenConvertedToImages;
    const numPages = doc?.file?.numberOfPages ?? 0;

    // Get search matches
    const { data: searchMatches } = useGetDocChunkMatchesV2(matterId, docId, searchQuery);

    // Combine fact highlights with search highlights
    const { allHighlights, searchChunks } = useMemo(() => {
      // When searching, only show search highlights
      const combinedHighlights = searchQuery ? [] : [...highlights];
      const chunks: { chunkId: string; boundingBoxIds: string[] }[] = [];

      if (searchMatches?.chunks) {
        searchMatches.chunks.forEach((chunk) => {
          const chunkBoundingBoxIds: string[] = [];
          if (chunk.boundingBoxes) {
            chunk.boundingBoxes.forEach((box, boxIndex) => {
              const searchId = `search-${chunk.id}-${box.pageNumber}-${boxIndex}`;
              chunkBoundingBoxIds.push(searchId);
              combinedHighlights.push({
                ...box,
                id: searchId,
              });
            });
          }
          if (chunkBoundingBoxIds.length > 0) {
            chunks.push({ chunkId: chunk.id, boundingBoxIds: chunkBoundingBoxIds });
          }
        });
      }

      return { allHighlights: combinedHighlights, searchChunks: chunks };
    }, [highlights, searchMatches, searchQuery]);

    // Calculate total search matches (chunks, not bounding boxes)
    const totalSearchMatches = searchChunks.length;

    // Reset current match index when search results change
    useEffect(() => {
      if (searchQuery) {
        setCurrentMatchIndex(0);
        setHighlightedChunkId(null);
      }
    }, [searchMatches]);

    // Track current index when navigating through a fact's multiple page highlights
    const [currentFactHighlightIndex, setCurrentFactHighlightIndex] = useState(0);

    // Compute sorted unique highlights for the currently highlighted fact
    const factHighlightsForCurrentFact = useMemo(() => {
      if (!highlightedFactId) return [];

      // Get all highlights for this fact, sorted by page then by top position
      const factHighlights = allHighlights
        .filter((h) => h.id === highlightedFactId)
        .sort((a, b) => {
          const pageCompare = a.pageNumber - b.pageNumber;
          if (pageCompare !== 0) return pageCompare;
          return a.top - b.top;
        });

      return factHighlights;
    }, [highlightedFactId, allHighlights]);

    // Get unique pages for the current fact (for display purposes)
    const uniquePagesForCurrentFact = useMemo(() => {
      const pages = new Set(factHighlightsForCurrentFact.map((h) => h.pageNumber));
      return Array.from(pages).sort((a, b) => a - b);
    }, [factHighlightsForCurrentFact]);

    // Reset highlight index when fact changes
    useEffect(() => {
      setCurrentFactHighlightIndex(0);
    }, [highlightedFactId]);

    // Calculate max width across all pages for default zoom
    const getMaxPageWidth = useCallback(() => {
      if (!doc?.file || numPages === 0) return DEFAULT_PAGE_WIDTH;

      let maxWidth = 0;

      if (doc.file.pageDimensions) {
        for (let i = 0; i < numPages; i++) {
          const pageDim = doc.file.pageDimensions[i];
          if (pageDim) {
            const scaleRatio = pageDim.height / DEFAULT_PAGE_HEIGHT;
            const normalizedWidth = pageDim.width / scaleRatio;
            maxWidth = Math.max(maxWidth, normalizedWidth);
          }
        }
      } else {
        const scaleRatio = (doc.file.height ?? DEFAULT_PAGE_HEIGHT) / DEFAULT_PAGE_HEIGHT;
        maxWidth = (doc.file.width ?? DEFAULT_PAGE_WIDTH) / scaleRatio;
      }

      return maxWidth || DEFAULT_PAGE_WIDTH;
    }, [doc?.file, numPages]);

    const getPageDimensions = useCallback(
      (pageNumber: number) => {
        if (doc?.file?.pageDimensions && doc.file.pageDimensions[pageNumber - 1]) {
          const pageDim = doc.file.pageDimensions[pageNumber - 1];
          const scaleRatio = pageDim.height / DEFAULT_PAGE_HEIGHT;
          return {
            height: DEFAULT_PAGE_HEIGHT * zoom,
            width: (pageDim.width / scaleRatio) * zoom,
          };
        }

        const scaleRatio = (doc?.file.height ?? DEFAULT_PAGE_HEIGHT) / DEFAULT_PAGE_HEIGHT;
        return {
          height: DEFAULT_PAGE_HEIGHT * zoom,
          width: ((doc?.file.width ?? DEFAULT_PAGE_WIDTH) / scaleRatio) * zoom,
        };
      },
      [doc?.file, zoom],
    );

    // Precompute page positions, total height, and max width in a single pass
    const { totalHeight, maxPageWidth, pagePositions } = useMemo(() => {
      const positions: number[] = [0]; // positions[0] is unused, positions[1] = position of page 1
      let height = 0;
      let maxWidth = 0;

      for (let i = 1; i <= numPages; i++) {
        positions[i] = height;
        const pageDims = getPageDimensions(i);
        height += pageDims.height + 10; // 10px gap between pages
        maxWidth = Math.max(maxWidth, pageDims.width);
      }

      return { totalHeight: height, maxPageWidth: maxWidth, pagePositions: positions };
    }, [numPages, getPageDimensions]);

    const getOptimalZoom = useCallback(() => {
      if (!containerRef.current || !doc?.file) return 1;

      const container = containerRef.current;
      const availableWidth = container.clientWidth - 40; // Leave some padding
      const maxWidth = getMaxPageWidth();
      const calculatedZoom = availableWidth / maxWidth;
      const finalZoom = Math.min(calculatedZoom, 4); // Cap at 4x zoom

      return finalZoom;
    }, [getMaxPageWidth, doc?.file]);

    // Get optimal zoom when file loads
    useEffect(() => {
      if (!containerRef.current || !doc?.file) return;

      const container = containerRef.current;

      // Initial zoom calculation if user hasn't changed zoom
      if (container.clientWidth > 100 && !userHasChangedZoom) {
        const optimalZoom = getOptimalZoom();
        setZoom(optimalZoom);
      }
    }, [doc?.file, userHasChangedZoom]);

    // Reset user zoom tracking when document changes
    useEffect(() => {
      if (doc?.file) {
        setUserHasChangedZoom(false);
      }
    }, [doc?.file]);

    // Add trackpad zoom functionality
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const handleWheel = (event: WheelEvent) => {
        // Check if this is a zoom gesture (Ctrl/Cmd key pressed or trackpad pinch)
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();

          // Calculate zoom delta based on wheel direction
          const delta = -event.deltaY * 0.01;
          handleZoom(delta);
        }
      };

      container.addEventListener('wheel', handleWheel, { passive: false });

      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }, [handleZoom]);

    const visiblePages = useVisiblePages(containerRef, numPages, getPageDimensions);
    const [debouncedVisiblePages] = useDebounce(visiblePages, 30);

    // O(1) lookup using precomputed positions instead of O(n) loop
    const getPagePosition = useCallback((pageNumber: number) => pagePositions[pageNumber] ?? 0, [pagePositions]);

    // Handle scrolling to a highlight when the scrollToHighlightId changes
    const calculateScrollPosition = useCallback(
      (highlight: BoundingBoxWithId) => {
        const pageDims = getPageDimensions(highlight.pageNumber);
        // Calculate the scroll position using the top and the page number
        // Subtract 10% of page height for padding above the highlight
        return highlight.top * pageDims.height + getPagePosition(highlight.pageNumber) - pageDims.height * 0.1;
      },
      [getPageDimensions, getPagePosition],
    );

    const scrollToHighlight = useCallback(
      (id: string) => {
        if (!containerRef.current) return;

        const highlight = allHighlights.find((h) => h.id === id);
        if (!highlight) return;

        const scrollY = calculateScrollPosition(highlight);

        if (setHighlightedFactId) {
          setHighlightedFactId(id);
        }

        containerRef.current.scrollTo({ top: Math.max(0, scrollY), behavior: 'smooth' });
      },
      [calculateScrollPosition, setHighlightedFactId, allHighlights],
    );

    useEffect(() => {
      if (highlightedFactId) {
        scrollToHighlight(highlightedFactId);
      }
    }, [highlightedFactId, allHighlights, containerRef.current]);

    // Navigate to the next highlight within the current fact's pages (loops around)
    const handleNextFactHighlight = useCallback(() => {
      if (uniquePagesForCurrentFact.length === 0) return;

      const nextIndex = (currentFactHighlightIndex + 1) % uniquePagesForCurrentFact.length;
      setCurrentFactHighlightIndex(nextIndex);

      // Find the first highlight on the target page
      const targetPage = uniquePagesForCurrentFact[nextIndex];
      const targetHighlight = factHighlightsForCurrentFact.find((h) => h.pageNumber === targetPage);

      if (targetHighlight && containerRef.current) {
        const scrollY = calculateScrollPosition(targetHighlight);
        containerRef.current.scrollTo({ top: Math.max(0, scrollY), behavior: 'smooth' });
      }
    }, [uniquePagesForCurrentFact, currentFactHighlightIndex, factHighlightsForCurrentFact, calculateScrollPosition]);

    // Navigate to the previous highlight within the current fact's pages (loops around)
    const handlePreviousFactHighlight = useCallback(() => {
      if (uniquePagesForCurrentFact.length === 0) return;

      const prevIndex =
        currentFactHighlightIndex <= 0 ? uniquePagesForCurrentFact.length - 1 : currentFactHighlightIndex - 1;
      setCurrentFactHighlightIndex(prevIndex);

      // Find the first highlight on the target page
      const targetPage = uniquePagesForCurrentFact[prevIndex];
      const targetHighlight = factHighlightsForCurrentFact.find((h) => h.pageNumber === targetPage);

      if (targetHighlight && containerRef.current) {
        const scrollY = calculateScrollPosition(targetHighlight);
        containerRef.current.scrollTo({ top: Math.max(0, scrollY), behavior: 'smooth' });
      }
    }, [uniquePagesForCurrentFact, currentFactHighlightIndex, factHighlightsForCurrentFact, calculateScrollPosition]);

    const handleSelectionComplete = useCallback(
      (boundingBox: BoundingBox, imageData: string, selectionBox: SelectionBox, pageNumber: number) => {
        const pageElement = document.querySelector(`[data-page-number="${pageNumber}"]`);

        if (pageElement && containerRef.current) {
          const pageRect = pageElement.getBoundingClientRect();
          const containerRect = containerRef.current.getBoundingClientRect();

          // Determine side based on PDF viewer position relative to viewport
          const viewportWidth = window.innerWidth;
          const pdfViewerLeftEdge = containerRect.left;
          const isLeftHalf = pdfViewerLeftEdge < viewportWidth * 0.5;
          const side = isLeftHalf ? 'right' : 'left';

          // Position anchor based on determined side
          const anchorX =
            side === 'right'
              ? pageRect.left - containerRect.left + selectionBox.endX // Right edge of selection
              : pageRect.left - containerRect.left + selectionBox.startX; // Left edge of selection

          const anchorY =
            pageRect.top - containerRect.top + selectionBox.startY + (selectionBox.endY - selectionBox.startY) / 2;

          setPopupData({
            isOpen: true,
            imageBase64: imageData,
            boundingBox,
            anchorPosition: { x: anchorX, y: anchorY, pageNumber },
            side,
          });
          setActiveSelection({ box: selectionBox, pageNumber });
          setIsSelectionMode(false);
        }
      },
      [],
    );

    const handlePopupClose = useCallback(() => {
      setPopupData({
        isOpen: false,
        imageBase64: '',
        boundingBox: null,
        anchorPosition: null,
        side: 'left',
      });
      setActiveSelection(null);
    }, []);

    const handleFactExtractionSuccess = useCallback(() => {
      // Refresh facts or show success message
      handlePopupClose();
    }, [handlePopupClose]);

    // Search functions
    const handleSearch = useCallback((query: string) => {
      setSearchQuery(query);
      setCurrentMatchIndex(0);
    }, []);

    const navigateToMatch = useCallback(
      (targetIndex: number) => {
        if (totalSearchMatches > 0 && targetIndex >= 0 && targetIndex < totalSearchMatches) {
          setCurrentMatchIndex(targetIndex);

          // Get the current chunk and highlight all its bounding boxes
          const currentChunk = searchChunks[targetIndex];
          if (currentChunk && currentChunk.boundingBoxIds.length > 0) {
            setHighlightedChunkId(currentChunk.chunkId);
            // Scroll to the first bounding box of this chunk
            const firstBoxId = currentChunk.boundingBoxIds[0];
            const firstBox = allHighlights.find((h) => h.id === firstBoxId);
            if (firstBox && containerRef.current) {
              const scrollY = calculateScrollPosition(firstBox);
              containerRef.current.scrollTo({ top: Math.max(0, scrollY), behavior: 'smooth' });
            }
          }
        }
      },
      [totalSearchMatches, searchChunks, allHighlights, calculateScrollPosition],
    );

    const handleNextMatch = useCallback(() => {
      if (totalSearchMatches > 0) {
        const safeCurrentIndex = Math.min(currentMatchIndex, totalSearchMatches - 1);
        const nextIndex = (safeCurrentIndex + 1) % totalSearchMatches;
        navigateToMatch(nextIndex);
      }
    }, [currentMatchIndex, totalSearchMatches, navigateToMatch]);

    const handlePreviousMatch = useCallback(() => {
      if (totalSearchMatches > 0) {
        const safeCurrentIndex = Math.min(currentMatchIndex, totalSearchMatches - 1);
        const prevIndex = safeCurrentIndex === 0 ? totalSearchMatches - 1 : safeCurrentIndex - 1;
        navigateToMatch(prevIndex);
      }
    }, [currentMatchIndex, totalSearchMatches, navigateToMatch]);

    const handleCloseSearch = useCallback(() => {
      setShowSearchBar(false);
      setSearchQuery('');
      setCurrentMatchIndex(0);
      setHighlightedChunkId(null);
    }, []);

    // Scroll to first search result when search matches are loaded
    useEffect(() => {
      if (searchQuery && totalSearchMatches > 0 && currentMatchIndex === 0) {
        navigateToMatch(0);
      }
    }, [searchQuery, totalSearchMatches, currentMatchIndex, navigateToMatch]);

    // Expose clearSearch method to parent
    useImperativeHandle(
      ref,
      () => ({
        clearSearch: () => {
          handleCloseSearch();
        },
      }),
      [handleCloseSearch],
    );

    if (!matterId) {
      return null;
    }

    return (
      <>
        <div className="absolute top-0 right-0 left-0 z-30 m-2.5 flex items-start justify-between gap-1.5">
          <div className="flex flex-wrap gap-1.5">
            <Zoom handleZoomOut={handleZoomOut} handleZoomIn={handleZoomIn} />
            <SearchButton showSearchBar={showSearchBar} onClick={() => setShowSearchBar(!showSearchBar)} />
            {hasBeenConvertedToImages && !hideSnippingTool && (
              <ExtractFactsFromSelectionButton
                isSelectionMode={isSelectionMode}
                setIsSelectionMode={setIsSelectionMode}
                docId={docId}
              />
            )}
            <DownloadIcon
              matterId={matterId}
              docId={docId}
              fileName={fileName}
              isProcessed={doc?.isProcessed}
              fileExtension={doc?.file?.extension}
            />
            {!hideFeatureButtons && showFacts !== undefined && setShowFacts !== undefined && (
              <>
                <FullDocLink docId={docId} />
                <ShowFactsIcon showFacts={showFacts} setShowFacts={setShowFacts} />
              </>
            )}
          </div>

          <div className="flex items-center gap-2.5">
            <AnimatePresence mode="wait">
              {showSearchBar ? (
                <motion.div
                  key="search-bar"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15 }}
                >
                  <SearchBar
                    onSearch={handleSearch}
                    onNext={handleNextMatch}
                    onPrevious={handlePreviousMatch}
                    onClose={handleCloseSearch}
                    currentMatch={Math.min(currentMatchIndex + 1, totalSearchMatches)}
                    totalMatches={totalSearchMatches}
                    initialQuery={initialSearchQuery}
                  />
                </motion.div>
              ) : highlightedFactId ? (
                <motion.div
                  key="fact-info"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 p-1.5 pl-3 text-xs text-gray-700 shadow-lg/3"
                >
                  <Icon className="lucide--info text-teal-500" />
                  <div className="whitespace-nowrap">
                    Fact sourced from <span className="font-medium">{uniquePagesForCurrentFact.length}</span>{' '}
                    {uniquePagesForCurrentFact.length === 1 ? 'page' : 'pages'}{' '}
                    <span className="text-gray-400">
                      ({currentFactHighlightIndex + 1}/{uniquePagesForCurrentFact.length})
                    </span>
                  </div>

                  <div>
                    <ButtonGroup>
                      <Button
                        size="iconSm"
                        className="bg-white hover:border-gray-400 hover:bg-white"
                        onClick={handlePreviousFactHighlight}
                      >
                        <Icon className="lucide--arrow-up" />
                      </Button>
                      <Button
                        size="iconSm"
                        className="bg-white hover:border-gray-400 hover:bg-white"
                        onClick={handleNextFactHighlight}
                      >
                        <Icon className="lucide--arrow-down" />
                      </Button>
                    </ButtonGroup>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>

            {closeViewer && <CloseIcon onClick={closeViewer} className="relative m-0" />}
          </div>
        </div>

        <div ref={containerRef} className="relative h-full overflow-auto" onClick={onViewerClick}>
          <div className="inline-block h-full min-w-full bg-gray-100">
            {isDocLoading ? (
              <DocViewerLoader />
            ) : (
              <>
                {hasBeenConvertedToImages ? (
                  <>
                    <ImageRenderer
                      docId={docId}
                      getPageDimensions={getPageDimensions}
                      visiblePages={debouncedVisiblePages}
                      highlights={allHighlights}
                      getPagePosition={getPagePosition}
                      highlightedFactId={highlightedFactId}
                      onHighlightClick={onHighlightClick}
                      highlightedChunkId={highlightedChunkId}
                      totalHeight={totalHeight}
                      maxPageWidth={maxPageWidth}
                      isSelectionMode={isSelectionMode}
                      onSelectionComplete={handleSelectionComplete}
                      maintainSelection={popupData.isOpen}
                      activeSelection={activeSelection}
                    />
                  </>
                ) : (
                  <PDFDocumentRenderer
                    docId={docId}
                    getPageDimensions={getPageDimensions}
                    visiblePages={visiblePages}
                    highlights={allHighlights}
                    getPagePosition={getPagePosition}
                    highlightedFactId={highlightedFactId}
                    onHighlightClick={onHighlightClick}
                    totalHeight={totalHeight}
                    zoom={zoom}
                  />
                )}
              </>
            )}
          </div>

          {/* minimap */}
          {/* <div className="sticky top-0 h-full w-1/5 max-w-16 bg-red-500"></div> */}
        </div>
        {popupData.isOpen && popupData.boundingBox && popupData.anchorPosition && (
          <div
            style={{
              position: 'absolute',
              left: popupData.anchorPosition.x,
              top: popupData.anchorPosition.y,
              width: 1,
              height: 1,
              pointerEvents: 'none',
              zIndex: 50,
            }}
          >
            <ExtractFactFromImagePopup
              isOpen={popupData.isOpen}
              onClose={handlePopupClose}
              docId={docId}
              imageBase64={popupData.imageBase64}
              boundingBox={popupData.boundingBox}
              onSuccess={handleFactExtractionSuccess}
              side={popupData.side}
            />
          </div>
        )}
      </>
    );
  },
);
